import React from "react"
import { useTranslation } from "react-i18next"
import { Pressable, StyleSheet, View } from "react-native"
import { useStyle } from "react-native-style-utilities"

import {
  AlertBodyText,
  AlertCard,
  AppIcons,
  AppText,
  Badge,
  Box,
  CancelButton,
  CardHeader,
  ListItemTextSecondary,
  Row,
  Spinner,
  Theme,
  useClipboard,
  useIsDarkMode,
} from "@/theme"

import { RunProgressBar } from "./RunProgressBar"

import { VfdIndicatorLarge } from "./VfdIndicator"

import type {
  AcceptsChildren,
  AppTextProps,
  BadgeProps,
  BoxProps,
  NoChildren,
  RowProps,
} from "@/theme"
import type { ViewProps } from "react-native"

import { clearDeviceUnreachableAsync } from "@/base/farmhq-api"
import { formatLastCheckInText } from "@/base/format-relative-time"
import type { InstallationType } from "@/base/sensor-configurations"
import type { SensorState } from "@/base/sensor-events"
import { getGrafanaUrlForDevice } from "@/constants/grafana"
import type { DeviceSummary } from "@/redux/device-configurations.reducer"
import type {
  DeviceStatusData,
  SensorStatusBadges,
  StatusBadgeProps,
} from "@/redux/device-event-last.reducer"
import type * as Models from "@/redux/models"
import { canScheduleActions } from "@/redux/named-device-actions.reducer"
import { getIsAdminModeEnabledFromState } from "@/redux/selectors"
import { useRootDispatch } from "@/redux/useRootDispatch"
import { useRootSelector } from "@/redux/useRootSelector"
import type { Href } from "expo-router"
import { Link } from "expo-router"
import { getExpandedBadgeKeys } from "./DeviceListItemCondensed.helpers"
import { InstallationTypeIndicator } from "./InstallationTypeIndicator"
/**
 *
 */
export function StatusBadge({
  colorScheme = "$gray",
  IconComponent,
  id,
  text,
  ...rest
}: StatusBadgeProps) {
  if (typeof text === "string") {
    return (
      <Badge
        StartIconComponent={IconComponent}
        colorScheme={colorScheme}
        id={id}
        style={{ marginVertical: Theme.SIZES.$1 }}
        {...rest}
      >
        {text}
      </Badge>
    )
  }
  return null
}

/**
 * Display the last check in time
 */
export function LastCheckInText({
  deviceEventTimestamp,
  ...rest
}: NoChildren<AppTextProps> & {
  deviceEventTimestamp: string | undefined
}) {
  return (
    <ListItemTextSecondary fontSize={"$sm"} {...rest}>
      {formatLastCheckInText({ deviceEventTimestamp })}
    </ListItemTextSecondary>
  )
}
export function DeviceUpdating(props: AcceptsChildren) {
  const { t } = useTranslation("devices")
  return (
    <View>
      {props.children}
      <Row>
        <Box mr={"$2"}>
          <Spinner />
        </Box>
        <AppText colorScheme={"secondary"} flex={1}>
          {t("waitingForUpdate")}
        </AppText>
      </Row>
    </View>
  )
}
/**
 * Display the device name and a loading spinner if it is updating
 */
export function DeviceNameText({
  _textProps,
  codaDeviceAlias,
  deviceName,
  isUpdating,
  ...rest
}: BoxProps & {
  _textProps?: AppTextProps
  codaDeviceAlias: string
  deviceName: string
  isUpdating: boolean
}) {
  let element = (
    <React.Fragment>
      <AppText {..._textProps} isTruncated>
        {deviceName}
      </AppText>
      {codaDeviceAlias === deviceName ? null : (
        <AppText isTruncated colorScheme={"secondary"} fontSize={"$xs"}>
          {codaDeviceAlias}
        </AppText>
      )}
    </React.Fragment>
  )
  if (isUpdating) {
    element = <DeviceUpdating>{element}</DeviceUpdating>
  }
  return <Box {...rest}>{element}</Box>
}

/**
 * Only shown for admin users
 */
function AdminInfoText(props: AppTextProps) {
  return <ListItemTextSecondary fontSize={"$xs"} {...props} />
}

/**
 * Only shown for admin users
 */
export function AdminInfo({
  deviceId,
  firmwareVersion,
  hardwareGeneration,
  ...rest
}: Pick<DeviceSummary, "deviceId" | "firmwareVersion" | "hardwareGeneration"> &
  ViewProps) {
  const isAdminModeEnabled = useRootSelector(getIsAdminModeEnabledFromState)
  const onCopy = useClipboard()
  if (!isAdminModeEnabled) {
    return null
  }
  return (
    <View {...rest}>
      <Row>
        <AdminInfoText>{`HW: ${hardwareGeneration}`}</AdminInfoText>
        <Box mr={"$2"}>
          <AdminInfoText>{`FW: ${firmwareVersion ?? "null"}`}</AdminInfoText>
        </Box>
      </Row>
      <Pressable onPress={() => onCopy(deviceId)}>
        <AdminInfoText>{`ID: ${deviceId}`}</AdminInfoText>
      </Pressable>
    </View>
  )
}

/**
 * Display Show the next scheduled action
 */
export function NextScheduledActionText({
  deviceInstallationType,
  nextScheduledAction,
  ...rest
}: BoxProps & {
  deviceInstallationType: InstallationType
  nextScheduledAction: Models.ScheduledDeviceAction | null | undefined
}) {
  const { t } = useTranslation("schedules")

  let text: string | undefined
  if (nextScheduledAction) {
    text = t("nextScheduleActionWithTimestamp", {
      hour: "numeric",
      minute: "numeric",
      // weekday: "auto",
      timestamp: new Date(
        // TODO: we multiply this by 1000 because it is in seconds but
        // that hopefully will change to become a string
        typeof nextScheduledAction.executeAtTs === "number"
          ? nextScheduledAction.executeAtTs * 1000
          : nextScheduledAction.executeAtTs,
      ),
    })
  } else if (canScheduleActions(deviceInstallationType)) {
    text = t("noScheduledActions", { ns: "common" })
  }

  if (typeof text === "string") {
    return (
      <Row {...rest}>
        <Box mr={"$2"}>
          <AppIcons.Clock size={14} />
        </Box>
        <AppText colorScheme={"secondary"} fontSize={"$sm"}>
          {text}
        </AppText>
      </Row>
    )
  }
  return null
}

export function BatteryCriticalWarning({
  batteryState,
  ...rest
}: BoxProps & {
  batteryState: SensorState<"battery"> | null | undefined
}) {
  const { t } = useTranslation()
  if (batteryState === "BES") {
    return (
      <AlertCard
        py={"$1.5"}
        severity={"error"}
        titleText={t("warning")}
        {...rest}
      >
        <AlertBodyText colorScheme={"darkText"} fontSize={"$sm"}>
          {t("BES.long", { ns: "sensorStates" })}
        </AlertBodyText>
      </AlertCard>
    )
  }
  return null
}

export function DeviceOfflineBadge(props: RowProps) {
  const { t } = useTranslation("devices")
  return (
    <Row {...props}>
      <Box mr={"$2"}>
        <AppIcons.DeviceOffline />
      </Box>
      <AppText colorScheme={"secondary"}>{t("deviceOffline")}</AppText>
    </Row>
  )
}

export function DeviceUnreachableWarning({
  deviceId,
  unreachableUntil,
  ...rest
}: BoxProps & {
  deviceId: string
  unreachableUntil: string
}) {
  const { t } = useTranslation("devices")
  const dispatch = useRootDispatch()
  const mutedUntilDate = new Date(unreachableUntil).toLocaleString()
  return (
    <AlertCard
      py={"$1.5"}
      severity={"warning"}
      titleText={t("deviceUnreachable")}
      {...rest}
    >
      <AlertBodyText colorScheme={"darkText"} fontSize={"$sm"}>
        {t("deviceUnreachableDescription", { date: mutedUntilDate })}
      </AlertBodyText>
      <Box mt={"$2"}>
        <CancelButton
          onPress={() => {
            dispatch(clearDeviceUnreachableAsync({ deviceId }))
          }}
          text={t("clearUnreachable")}
        />
      </Box>
    </AlertCard>
  )
}

/**
 * Display badges in per-type priority order, falling back to the global
 * order from `getExpandedBadgeKeys` for anything not in the per-type list.
 */
export function StatusBadges({
  badgeProps,
  badges,
  deviceInstallationType,
  deviceStatus,
  omit,
  ...rest
}: NoChildren<BoxProps> & {
  badgeProps?: Omit<BadgeProps, "id">
  badges: SensorStatusBadges
  deviceInstallationType: InstallationType
  deviceStatus: DeviceStatusData["deviceStatus"]
  omit?: Array<keyof SensorStatusBadges> | keyof SensorStatusBadges
}) {
  if (deviceStatus === "offline") {
    return (
      <Box m={"auto"} py={"$2"} {...rest}>
        <DeviceOfflineBadge />
      </Box>
    )
  }

  const orderedKeys = getExpandedBadgeKeys({ deviceInstallationType })

  // filter out undefined badges so that the don't interfere with spacing
  let toRender = orderedKeys.filter((key) => Boolean(badges[key]))

  const omitArray = typeof omit === "string" ? [omit] : omit
  // Remove omitted badges if necessary. vfdStatus is intentionally absent
  // from `orderedKeys` since it renders separately as VfdIndicatorLarge.
  if (omitArray) {
    toRender = toRender.filter((key) => !omitArray.includes(key))
  }
  if (toRender.length === 0) {
    return null
  }

  return (
    <React.Fragment>
      <Row {...rest} alignItems={"baseline"} mb={"$2"} mx={"$-1"}>
        {toRender.map((key): React.JSX.Element | null => {
          const badge = badges[key]
          if (badge) {
            return (
              <Box key={key} mx={"$1"}>
                <StatusBadge {...badge} {...badgeProps} />
              </Box>
            )
          }
          return null
        })}
      </Row>
      {badges.vfdStatus ? <VfdIndicatorLarge {...badges.vfdStatus} /> : null}
    </React.Fragment>
  )
}

const styles = StyleSheet.create({
  commentDisplay: {
    alignItems: "center",
    backgroundColor: Theme.COLORS.$background.light,
    borderColor: Theme.COLORS.$gray[300],
    borderRadius: Theme.RADII.$default,
    borderWidth: 0.5,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "flex-start",
    paddingHorizontal: Theme.SPACING.$2,
    paddingVertical: Theme.SPACING.$2,
  },
  deviceNameText: { minWidth: 200 },
})

/**
 * Display a device comment
 */
export function DeviceCommentDisplay({
  contentText,
  createDate,
  createdByUser: { email, nameFamily, nameGiven },
  deleteButtonElement,
  editButtonElement,
  id,
  ...rest
}: Models.DeviceComment &
  Omit<RowProps, "id"> & {
    deleteButtonElement?: React.JSX.Element
    editButtonElement?: React.JSX.Element
  }) {
  const { t } = useTranslation()
  const isDark = useIsDarkMode()
  const fontSize = "$sm"

  const createdByElement = (
    <Row minW={"$16"}>
      <Box mr={"$2"}>
        <AppText colorScheme={"secondary"} fontSize={fontSize}>
          {typeof nameGiven === "string" && typeof nameFamily === "string"
            ? `${nameGiven} ${nameFamily}`
            : (email ?? "")}
        </AppText>
      </Box>
      <AppText colorScheme={"secondary"} fontSize={fontSize}>
        {t("datetimeWithVal", {
          dateStyle: "short",
          timeStyle: "short",
          val: new Date(createDate),
        })}
      </AppText>
    </Row>
  )

  return (
    <Row
      key={id}
      style={useStyle(() => {
        return [
          styles.commentDisplay,
          isDark
            ? {
                backgroundColor: Theme.COLORS.$background.dark,
              }
            : {},
        ]
      }, [isDark])}
      {...rest}
    >
      <AppIcons.Comment />
      <Box flex={1} minW={"$24"} mx={"$2"}>
        <AppText fontSize={fontSize}>{contentText}</AppText>
        {createdByElement}
      </Box>
      <Row>
        <Box mr={"$2"}>{editButtonElement}</Box>
        {deleteButtonElement}
      </Row>
    </Row>
  )
}

/**
 * Shows device name and current status
 */
export function DeviceListItem({
  badges,
  batteryState,
  codaDeviceAlias,
  commentActive,
  deviceEventTimestamp,
  deviceId,
  deviceInstallationType,
  deviceName,
  deviceStatus,
  firmwareVersion,
  hardwareGeneration,
  isUpdating,
  nextScheduledAction,
  runCompletion,
  showAdminInfo,
  showComment,
  showMetadata = true,
  unreachableUntil,
  ...rest
}: BoxProps &
  DeviceStatusData & {
    showAdminInfo: boolean
    showComment: boolean
    showMetadata?: boolean
  }): React.JSX.Element {
  const isUnreachable =
    unreachableUntil != null && new Date(unreachableUntil) > new Date()
  return (
    <Box py={"$2"} {...rest}>
      {showMetadata ? (
        <Row alignItems={"flex-start"} justifyContent={"space-between"}>
          <DeviceNameText
            codaDeviceAlias={codaDeviceAlias}
            deviceName={deviceName}
            flex={1}
            isUpdating={isUpdating}
            style={styles.deviceNameText}
          />
          <InstallationTypeIndicator value={deviceInstallationType} />
        </Row>
      ) : null}
      {isUnreachable ? (
        <DeviceUnreachableWarning
          deviceId={deviceId}
          my={"$1"}
          unreachableUntil={unreachableUntil!}
        />
      ) : null}
      <Box my={"$1"}>
        <LastCheckInText
          deviceEventTimestamp={deviceEventTimestamp}
          fontSize={"$xs"}
        />
      </Box>
      {showAdminInfo ? (
        <AdminInfo
          deviceId={deviceId}
          firmwareVersion={firmwareVersion}
          hardwareGeneration={hardwareGeneration}
        />
      ) : null}
      {showComment && commentActive ? (
        <Box mb={"$2"}>
          <DeviceCommentDisplay {...commentActive} />
        </Box>
      ) : null}
      <StatusBadges
        badges={badges}
        deviceInstallationType={deviceInstallationType}
        deviceStatus={deviceStatus}
        omit={["runEta", "batteryStateOfChargeInternal"]}
      />
      <BatteryCriticalWarning batteryState={batteryState} />
      <NextScheduledActionText
        deviceInstallationType={deviceInstallationType}
        nextScheduledAction={nextScheduledAction}
      />
      <Row w={"$full"}>
        <Box flexGrow={1} mr={"$2"}>
          {runCompletion ? <RunProgressBar {...runCompletion} /> : null}
        </Box>
        {badges.runEta ? <StatusBadge {...badges.runEta} /> : null}
      </Row>
      {showAdminInfo ? (
        <Box ml={"auto"} mt={"$2"}>
          <Link href={getGrafanaUrlForDevice({ codaDeviceAlias }) as Href}>
            {"Open in Grafana"}
          </Link>
        </Box>
      ) : null}
    </Box>
  )
}

export function NoDeviceLocation({
  onClose,
  ...rest
}: BoxProps & {
  onClose: () => void
}) {
  // TODO: Link to set permanent location
  const { t } = useTranslation("statusMap")
  return (
    <Box p={"$4"} {...rest}>
      <CardHeader
        IconComponent={"LocationPinned"}
        titleText={t("locateDevice.noLocationTitle")}
      />
      <Box mx={"auto"}>
        <AppText>{t("locateDevice.noLocationMessage")}</AppText>
      </Box>
      <Box mt={"$2"}>
        <CancelButton onPress={onClose} />
      </Box>
    </Box>
  )
}
